/**
 * Labels for the fixed path segments. A segment absent from this map is an
 * identifier — a workflow key or a task id — and is shown as it appears in the
 * address rather than invented.
 */
const SEGMENT_LABELS: Record<string, string> = {
  workflows: 'Workflows',
  // Both spellings, and both are still right (spec 094 FR-021). `board` is the
  // view — a board is still a board, and that word is not renamed. `boards` is
  // the kept redirect path: the section it named is gone, but the addresses are
  // not, and this is a pure function over a pathname rather than something that
  // only runs where a page rendered. Dropping the label did not stop it being
  // asked; it just made it answer `boards` in lower case.
  board: 'Board',
  boards: 'Boards',
  files: 'Files',
  tasks: 'Tasks',
  connections: 'Connections',
  triggers: 'Triggers',
  'agents-skills': 'Agents & Skills',
  library: 'Library',
  usage: 'Usage',
  'ai-models': 'AI models',
  // The grouped entries and their tabs (spec 224): Integrations › Connections.
  integrations: 'Integrations',
  'ai-usage': 'AI usage',
  models: 'Models',
  spend: 'Spend',
  secrets: 'Secrets',
  audit: 'Audit',
  settings: 'Settings',
  // Settings' tabs (spec 227): Settings › People, as Integrations › Connections.
  general: 'General',
  people: 'People',
  activity: 'Activity',
  labels: 'Labels',
  images: 'Execution images',
  builder: 'Builder',
  // The linked-workflow run page's own tail segment (spec 115 design doc
  // §4.2). The rest of that section's worded trail ("Workflows ›
  // <workflow name> › <task title> › Run") comes from `CrumbOverrides`
  // (below) — the run route passes the parent workflow's name/task's title
  // as `labels`, and omits the intervening `tasks` segment via `omit`.
  run: 'Run',
  'execution-images': 'Execution images',
};

/**
 * What the shell knows by name (spec 128 R1), so a crumb can say "Hello World
 * App" rather than `hello-world-app`. Optional: without it the trail is the
 * address's own words, as before.
 */
export interface CrumbNames {
  workspace?: string;
  /** By workflow key: its name and where it is used (board, or builder for a draft). */
  workflows: ReadonlyMap<string, { name: string; href: string }>;
  /** By task id: its title and where it opens. */
  tasks: ReadonlyMap<string, { title: string; href: string }>;
}

/** Shown for a name that has not arrived yet, rather than the raw id. */
export const PENDING_NAME = '…';

export interface Crumb {
  label: string;
  href: string;
}

export interface CrumbOverrides {
  /**
   * Per-segment display names, keyed by the raw path segment (an id or a
   * key), consulted before `SEGMENT_LABELS`/the raw-segment fallback. For a
   * route whose address names a resource by id rather than by a word this
   * function can label on its own — a workflow key, a task id — the page
   * that already has the resource's name in hand supplies it here, rather
   * than this pure, pathname-only function trying to fetch it itself.
   */
  labels?: Record<string, string>;
  /**
   * Raw path segments to drop from the trail entirely, rather than render as
   * a crumb of their own. For a route like .../tasks/$taskId/run, where
   * `tasks` names a path shape rather than a section a reader ever lands on,
   * a crumb for it would be a stop nothing points back to.
   */
  omit?: ReadonlySet<string>;
}

/**
 * Derives the breadcrumb trail from an address.
 *
 * Pure over `pathname` alone when `overrides` is omitted, so most callers —
 * and every existing test — stay exactly as unit-testable as before. A route
 * whose trail needs a fetched name or a collapsed segment (spec 115 design
 * doc §4.2) passes `overrides`; nothing else needs to.
 */
export function crumbsFromPathname(
  pathname: string,
  overrides?: CrumbOverrides,
  names?: CrumbNames,
): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'workspaces' || segments.length < 2) return [];

  const workspaceId = segments[1];
  const crumbs: Crumb[] = [
    {
      label: names?.workspace ?? 'Overview',
      href: `/workspaces/${workspaceId}`,
    },
  ];

  let href = `/workspaces/${workspaceId}`;
  const rest = segments.slice(2);
  rest.forEach((segment, index) => {
    href += `/${segment}`;
    if (overrides?.omit?.has(segment)) return;
    const previous = rest[index - 1];
    const fixed = overrides?.labels?.[segment] ?? SEGMENT_LABELS[segment];
    if (names) {
      // `workflows/{key}`: the workflow, by name, to where it is used. Ahead
      // of any page override, which can name it but not link it anywhere real.
      if (previous === 'workflows' && segment !== 'workflows') {
        const workflow = names.workflows.get(segment);
        if (workflow || !fixed) {
          crumbs.push({
            label: workflow?.name ?? PENDING_NAME,
            href: workflow?.href ?? href,
          });
          return;
        }
      }
      // `…/tasks/{id}` under a workflow names no page of its own; the task
      // opens on its board, and the bare `tasks` segment is skipped.
      if (segment === 'tasks' && rest[index - 2] === 'workflows') return;
      if (previous === 'tasks' && rest[index - 3] === 'workflows') {
        const task = names.tasks.get(segment);
        if (task || !fixed) {
          crumbs.push({
            label: task?.title ?? PENDING_NAME,
            href: task?.href ?? href,
          });
          return;
        }
      }
    }
    crumbs.push({ label: fixed ?? segment, href });
  });
  return crumbs;
}
